import { ChevronDownIcon, SquareArrowOutDownRightIcon } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useGetOrganizationSettings } from "~/api/organizations/organizations";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  EMAIL_EXPORT_MAX_CHARACTERS,
  EXPORT_LIST,
  EXPORT_TYPE,
} from "~/features/chat/constants";
import {
  downloadStringAsFile,
  downloadSummaryAsDocx,
  downloadSummaryAsPdf,
  sendSummaryWithEmail,
} from "~/features/chat/helpers";
import { trackEvent } from "~/utils/analytics";

type MarkdownExportProps = {
  organizationId: string;
  content: string;
  richContent?: string;
  fileName: string;
  disabled?: boolean;
  buttonSize?: "sm" | "mini";
  openEventName?: string;
  exportEventName?: string;
  transformContent?: (content: string) => string;
  title?: string;
  caseInfo?: {
    caseName: string;
    dateOfBirth: string;
    dateOfLoss: string;
    claimNumber: string;
  };
};

export const MarkdownExportButton: FC<MarkdownExportProps> = ({
  organizationId,
  content,
  richContent,
  fileName,
  disabled,
  buttonSize = "sm",
  openEventName,
  exportEventName,
  transformContent,
  title,
  caseInfo,
}) => {
  const [open, setOpen] = useState(false);

  const { data: settings } = useGetOrganizationSettings(organizationId);

  const contentToUse = useMemo(
    () => (transformContent ? transformContent(content) : content),
    [content, transformContent]
  );

  const richContentToUse = useMemo(
    () =>
      richContent
        ? transformContent
          ? transformContent(richContent)
          : richContent
        : contentToUse,
    [richContent, transformContent, contentToUse]
  );

  const availableExportOptions = useMemo(() => {
    const isEmailExportDisabled =
      contentToUse.length > EMAIL_EXPORT_MAX_CHARACTERS;
    return EXPORT_LIST.filter(
      (item) => !(item.type === EXPORT_TYPE.EMAIL && isEmailExportDisabled)
    );
  }, [contentToUse]);

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen && disabled) return;
    setOpen(newOpen);
  };

  const handleExport = (type: EXPORT_TYPE) => {
    if (exportEventName) {
      trackEvent({ eventName: exportEventName, properties: { type } });
    }
    switch (type) {
      case EXPORT_TYPE.DOCX:
        downloadSummaryAsDocx(
          richContentToUse,
          fileName,
          title,
          settings?.fontFamily,
          settings?.lineSpacing,
          caseInfo,
          settings?.logoUrl
        );
        break;
      case EXPORT_TYPE.PLAIN_TEXT:
        downloadStringAsFile(contentToUse, fileName);
        break;
      case EXPORT_TYPE.PDF:
        downloadSummaryAsPdf(
          richContentToUse,
          fileName,
          title || "Summary",
          caseInfo,
          settings?.fontFamily,
          settings?.lineSpacing,
          settings?.logoUrl,
          settings?.footnote
        );
        break;
      case EXPORT_TYPE.EMAIL:
        sendSummaryWithEmail(contentToUse, fileName);
        break;
    }
  };

  useEffect(() => {
    if (openEventName && open) {
      trackEvent({ eventName: openEventName });
    }
  }, [openEventName, open]);

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size={buttonSize} disabled={disabled}>
          <SquareArrowOutDownRightIcon className="w-4 h-4 mr-2" />
          <FormattedMessage id="common.export" defaultMessage="Export" />
          <ChevronDownIcon className="w-4 h-4 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuGroup>
          {availableExportOptions.map((item) => (
            <DropdownMenuItem
              key={item.label}
              onClick={() => handleExport(item.type)}
            >
              <FormattedMessage id={item.label} />
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
